import type { OperationsState } from "./operationsSchema";
import { lensLabel } from "./operationsLabels";

/**
 * The Operations screen's state as the assistant should read it: the same names the Operations
 * tools accept (lineOfBusiness, assignedTo, attention, stage…), with empty filters and unset drill
 * keys left out. The URL state uses terse keys (lob, eng, dAttention) that the model would have to
 * guess at; this translation removes the guessing.
 */
export interface OperationsAssistantContext {
  period: "today" | "weekToDate";
  filters: Record<string, string[] | boolean>;
  /** The attention lens, when one is on (same value the tools take). */
  lens?: "stuck" | "blocked" | "stuckAndBlocked" | "clear";
  /** Who holds the pen, when that lens is on (same value the tools take). */
  pen?: "internal" | "external";
}

const FILTER_KEYS: ReadonlyArray<[keyof OperationsState, string]> = [
  ["client", "client"],
  ["lob", "lineOfBusiness"],
  ["eng", "engagementType"],
  ["assigned", "assignedTo"],
  ["team", "team"],
  ["division", "division"],
  ["manager", "manager"],
  ["country", "workerCountry"],
  ["requestType", "requestType"],
];

const DRILL_KEYS: ReadonlyArray<[keyof OperationsState, string]> = [
  ["dAttention", "attention"],
  ["dStuckBy", "stuckBy"],
  ["dStage", "stage"],
  ["dStatus", "status"],
  ["dOwner", "owner"],
  ["dAgeBin", "ageBin"],
  ["dWorkType", "workType"],
  ["dFlow", "flow"],
  ["dJourneyFlow", "journeyFlow"],
  ["dStartRisk", "startRisk"],
  ["dDay", "day"],
  ["dDayKind", "dayKind"],
];

export function toAssistantContext(state: OperationsState): OperationsAssistantContext {
  const filters: Record<string, string[] | boolean> = {};
  for (const [stateKey, name] of FILTER_KEYS) {
    const value = state[stateKey];
    if (Array.isArray(value) && value.length > 0) filters[name] = value;
  }
  if (state.onboardingsOnly) filters.onboardingsOnly = true;
  if (state.redeployment) filters.redeployment = true;
  const lens = state.lens;
  const pen = state.pen;

  return {
    period: state.period === "today" ? "today" : "weekToDate",
    filters,
    ...(lens ? { lens } : {}),
    ...(pen ? { pen } : {}),
  };
}

/** The open drawer or tile in the tools' names (stage, attention, owner…), or undefined when nothing is open (2026-10-29: sent on its own). */
export function assistantDrill(state: OperationsState): Record<string, string | number> | undefined {
  const drill: Record<string, string | number> = {};
  for (const [stateKey, name] of DRILL_KEYS) {
    const value = state[stateKey];
    if (typeof value === "string" && value.length > 0) drill[name] = value;
    else if (typeof value === "number") drill[name] = value;
  }
  return Object.keys(drill).length > 0 ? drill : undefined;
}

/** "A", "A, B", "A, B +3": a slicer's values for a one-line scope. */
function listOf(values: string[]): string {
  return values.length <= 2 ? values.join(", ") : `${values.slice(0, 2).join(", ")} +${values.length - 2}`;
}

/**
 * The plain-language scope the Ask Claude panel answers for (2026-10-29, "Ask Claude for Ops leaders"): "Whole org"
 * without a slicer; otherwise who first (owner, then the team, the manager's teams, the division), then the other
 * slicers in the bar's order, joined with " · "; a lens adds "Stuck · External only". The team key is the lead's
 * name in the provisioning directory, so a team reads as "<lead>'s team".
 */
export function scopeLabel(state: OperationsState): string {
  const parts: string[] = [];
  if (state.assigned.length > 0) parts.push(listOf(state.assigned));
  if (state.team.length > 0) parts.push(state.team.length === 1 ? `${state.team[0]}'s team` : `Teams ${listOf(state.team)}`);
  if (state.manager.length > 0) parts.push(state.manager.length === 1 ? `${state.manager[0]}'s teams` : `Managers ${listOf(state.manager)}`);
  if (state.division.length > 0) parts.push(listOf(state.division));
  for (const values of [state.client, state.lob, state.eng, state.requestType, state.country]) {
    if (values.length > 0) parts.push(listOf(values));
  }
  if (state.onboardingsOnly) parts.push("Onboarding journeys");
  if (state.redeployment) parts.push("Redeployments");
  const scope = parts.length > 0 ? parts.join(" · ") : "Whole org";
  const lens = lensLabel(state.lens, state.pen);
  return lens ? `${scope} · ${lens} only` : scope;
}
